import logging from "utils/logging";
import React from "react";
import { 
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
 } from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { selectPlayerSubmissionsForJudging } from "features/question/submissionSlice";
import { useSelector } from "react-redux";
import { selectCurrentQuestionObject } from "../../question/questionSlice";
import Provider from "provider";
import {
  GAMESTATE_ROUND_FINAL,
  SUBMISSION_STATE_COMMITTED,
  SUBMISSION_STATE_CORRECT,
  SUBMISSION_STATE_WRONG,
} from "../../gamestate/constants";
import { selectGameStatusObject } from "../../gamestate/gameStateSlice";

const log = logging.getLogger("JudgingBoard");

const DemoList = styled(List)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
}));

const HeaderTypography = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const InlineTypography = styled(Typography)({
  display: "inline",
});

const PlayerRow = ({ player, submission, question, round }) => {

  let answer = "";
  let value = "";
  let primary = player ? player.name : "–";

  let pointValue = question.value;
  if (submission && submission.wager) {
    pointValue = submission.wager;
  }

  const approveSubmission = async () => {
    const { gameId, questionId, id } = submission;
    await Provider.updateSubmission(
      gameId,
      questionId,
      id,
      SUBMISSION_STATE_CORRECT,
      pointValue
    );
  };

  const rejectSubmission = async () => {
    const { gameId, questionId, id } = submission;
    await Provider.updateSubmission(
      gameId,
      questionId,
      id,
      SUBMISSION_STATE_WRONG,
      -pointValue
    );
  };

  let disableButtons = !submission;

  // Disable the judging buttons if there is no answer submitted
  if (submission && !submission.answer) {
    // Unless it's a daily double or final jeopardy
    if (question.dailyDouble || round === GAMESTATE_ROUND_FINAL) {
      disableButtons = false;
    } else {
      disableButtons = true;
    }
  }

  let approveButton = (
    <IconButton
      edge="end"
      aria-label="approve"
      onClick={approveSubmission}
      disabled={disableButtons}
    >
      <CheckCircleIcon />
    </IconButton>
  );

  let rejectButton = (
    <IconButton
      edge="end"
      aria-label="approve"
      onClick={rejectSubmission}
      disabled={disableButtons}
    >
      <CancelIcon />
    </IconButton>
  );

  if (submission) {
    let answerTime = "";
    if (question.startTime && submission.submittedAt) {
      const qTime = new Date(question.startTime);
      const aTime = new Date(submission.submittedAt);

      answerTime = "" + Math.abs(aTime - qTime) / 1000;
      answerTime = " — " + answerTime + "s";
    }

    if (submission.wager) {
      primary = (
        <React.Fragment>
          <span>{player.name}</span>
          {" | Wager: $"}
          <span>{submission.wager.toLocaleString()}</span>
        </React.Fragment>
      );
    }

    if (submission.status === SUBMISSION_STATE_CORRECT) {
      approveButton = (
        <IconButton
          edge="end"
          aria-label="approve"
          style={{ color: "#1bbc1b" }}
          disabled
        >
          <CheckCircleIcon />
        </IconButton>
      );
      value = (
        <span style={{ color: "#1bbc1b" }}>{" | +" + submission.points}</span>
      );
    } else if (submission.status === SUBMISSION_STATE_WRONG) {
      rejectButton = (
        <IconButton
          edge="end"
          aria-label="reject"
          style={{ color: "#bc1b1b" }}
          disabled
        >
          <CancelIcon />
        </IconButton>
      );
      value = (
        <span style={{ color: "#bc1b1b" }}>{" | " + submission.points}</span>
      );
    } else if (submission.status === SUBMISSION_STATE_COMMITTED) {
      rejectButton = (
        <IconButton edge="end" aria-label="reject" disabled>
          <CancelIcon />
        </IconButton>
      );
      approveButton = (
        <IconButton edge="end" aria-label="approve" disabled>
          <CheckCircleIcon />
        </IconButton>
      );

      value = <span>{" | " + submission.points}</span>;
    }
    answer = (
      <React.Fragment>
        <InlineTypography
          component="span"
          variant="body2"
          color="textPrimary"
        >
          {submission.answer}
        </InlineTypography>
        {answerTime}
        {value}
      </React.Fragment>
    );
  }

  return (
    <ListItem>
      <ListItemText primary={primary} secondary={answer} />

      <ListItemSecondaryAction>
        {approveButton}
        {rejectButton}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const JudgingBoard = () => {
  const submissions = useSelector(selectPlayerSubmissionsForJudging);
  const question = useSelector(selectCurrentQuestionObject);
  const gameState = useSelector(selectGameStatusObject);

  log.debug("Judging board submissions", submissions);

  return (
    <React.Fragment>
      <HeaderTypography variant="h5">
        Judging Board
      </HeaderTypography>
      <DemoList>
        {submissions ? submissions.filter(ps => ps.submission).map((playerSubmission, idx) => (
          <PlayerRow
            key={playerSubmission.submission?.id || `player-${playerSubmission.id || idx}`}
            player={playerSubmission}
            submission={playerSubmission.submission}
            question={question}
            round={gameState.round}
          />
        )) : (
          <ListItem>
            <ListItemText primary="No submissions to judge" />
          </ListItem>
        )}
      </DemoList>
    </React.Fragment>
  );
};

export default JudgingBoard;
